/* ──────────────────────────────────────────────────────────
   Main dashboard sections — bundled into one file for compactness.
   Each section is self-contained: fetches its own data, renders,
   handles local mutations.

   Exports: Roadmap, VisionQuestionnaire, Contract, Invoices,
            KickOffScheduler, FileStorage, Websites, Plans
   ────────────────────────────────────────────────────────── */
import React, { useEffect, useMemo, useState } from "react";
import api from "./api";
import { useAuth } from "./AuthContext";
import RoadmapChart from "./RoadmapChart";

const C = {
  bg:          "#000000",
  bgSecondary: "#0a0a0a",
  bgTertiary:  "#111111",
  glass:       "rgba(255,255,255,0.05)",
  glassStrong: "rgba(255,255,255,0.09)",
  border:      "rgba(255,255,255,0.08)",
  borderMed:   "rgba(255,255,255,0.14)",
  text:        "#F5F5F5",
  textSec:     "rgba(255,255,255,0.68)",
  textMuted:   "rgba(255,255,255,0.42)",
  textDim:     "rgba(255,255,255,0.24)",
  accent:      "#E5D3A3",
  accentGreen: "#83F28F",
  accentBlue:  "#A8D1FF",
  innerGlow:   "inset 0 1px 0 rgba(255,255,255,0.07)",
  // legacy aliases
  ink:  "#F5F5F5",
  mid:  "rgba(255,255,255,0.68)",
  soft: "rgba(255,255,255,0.50)",
  muted:"rgba(255,255,255,0.38)",
  faint:"rgba(255,255,255,0.16)",
};

// Gold NW→black→gold SE gradient — very subtle, just enough to feel dimensional
const GOLD_GRAD = "linear-gradient(135deg, rgba(229,211,163,0.055) 0%, rgba(0,0,0,0) 42%, rgba(0,0,0,0) 58%, rgba(229,211,163,0.04) 100%)";

/* ─── Card — glass surface with gold gradient overlay ────── */
function Card({ children, style, padding }) {
  return (
    <div style={{
      position: "relative",
      background: C.glass,
      backdropFilter: "blur(18px)",
      WebkitBackdropFilter: "blur(18px)",
      border: `1px solid ${C.border}`,
      borderRadius: 20,
      overflow: "hidden",
      boxShadow: C.innerGlow,
      ...style,
    }}>
      <div style={{ position: "absolute", inset: 0, background: GOLD_GRAD, borderRadius: 20, pointerEvents: "none", zIndex: 0 }} />
      <div style={{ position: "relative", zIndex: 1, padding: padding ?? 24 }}>{children}</div>
    </div>
  );
}

/* ─── Section wrapper ────────────────────────────────────── */
function Section({ title, eyebrow, right, children }) {
  return (
    <section style={{ marginBottom: 48 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 18, gap: 16 }}>
        <div>
          {eyebrow && <div style={S.eyebrow}>{eyebrow}</div>}
          <h2 style={S.h2}>{title}</h2>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function GatedNotice({ kind, title, body }) {
  return (
    <Card>
      <div style={S.eyebrow}>◌ &nbsp; {kind === "explore" ? "MEMBERS ONLY" : "ACCOUNT ATTENTION"}</div>
      <div style={{ ...S.gatedH, marginTop: 14 }}>{title}</div>
      <p style={{ ...S.gatedBody, marginTop: 10 }}>{body}</p>
    </Card>
  );
}

/* ═══════════ ROADMAP ═══════════════════════════════════ */
export function Roadmap() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/roadmap").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const { roadmap, kind } = data;

  const overallPct = roadmap.milestones.length
    ? Math.round(roadmap.milestones.reduce((s, m) => s + (m.percent || 0), 0) / roadmap.milestones.length)
    : 0;

  return (
    <Section
      eyebrow="◈ &nbsp; YOUR ROADMAP"
      title={roadmap.projectName}
    >
      {kind === "starter" && (
        <div style={{ ...S.eyebrow, marginBottom: 14, color: C.textMuted }}>
          Preview view — your custom roadmap appears once we begin together.
        </div>
      )}

      {/* Chart.js timeline — floats on the dark bg */}
      <RoadmapChart milestones={roadmap.milestones} accent={C.accent} ink={C.text} />

      {roadmap.notes && <div style={{ ...S.notes, marginTop: 18 }}>{roadmap.notes}</div>}
    </Section>
  );
}

/* ═══════════ VISION QUESTIONNAIRE ══════════════════════ */
const VQ_FIELDS = [
  { k: "vision",    label: "What does success look like for this project?", multiline: true },
  { k: "audience",  label: "Who is this for? Describe the people who will use it." },
  { k: "stage",     label: "Where are you today — concept, in-progress, or refining something existing?" },
  { k: "timeline",  label: "What's your ideal timeline?" },
  { k: "budget",    label: "Budget range you're working within (optional)?" },
  { k: "inspiration", label: "Any sites, brands, or work that capture the feel you want?", multiline: true },
  { k: "concerns",  label: "Anything you're worried about or want us to know?", multiline: true },
];

export function VisionQuestionnaire() {
  const { hasActiveAccess, effectiveStatus } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    api.get("/intake").then((r) => {
      setSubmission(r.data.submission);
      if (r.data.submission?.answers) setAnswers(r.data.submission.answers);
    }).catch(() => {});
  }, []);

  if (!hasActiveAccess) {
    return (
      <Section eyebrow="◌ &nbsp; VISION QUESTIONNAIRE" title="Tell us about your project.">
        <GatedNotice kind={effectiveStatus} title="Available with a membership."
          body="Your Vision Questionnaire helps us tailor the engagement around your specific goals, audience, and constraints. Activate your membership to begin." />
      </Section>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/intake", { answers });
      setSubmission(r.data.submission);
      setSavedAt(new Date());
    } catch {} finally { setBusy(false); }
  };

  return (
    <Section eyebrow="◈ &nbsp; VISION QUESTIONNAIRE" title="Tell us about your project."
      right={submission && <span style={S.eyebrow}>Last saved {new Date(submission.submittedAt).toLocaleString()}</span>}>
      <Card padding={28}>
        <form onSubmit={submit}>
          {VQ_FIELDS.map((f) => (
            <div key={f.k} style={{ marginBottom: 20 }}>
              <label style={S.fieldLabel}>{f.label}</label>
              {f.multiline
                ? <textarea style={{ ...S.input, minHeight: 90 }} value={answers[f.k] || ""} onChange={(e) => setAnswers({ ...answers, [f.k]: e.target.value })} />
                : <input style={S.input} value={answers[f.k] || ""} onChange={(e) => setAnswers({ ...answers, [f.k]: e.target.value })} />
              }
            </div>
          ))}
          <div style={{ marginTop: 24, display: "flex", alignItems: "center", gap: 16 }}>
            <button type="submit" disabled={busy} style={S.btnDark}>
              <span>{busy ? "Saving…" : submission ? "Update submission" : "Submit"}</span><span>→</span>
            </button>
            {savedAt && <span style={{ ...S.eyebrow, color: C.accentGreen }}>✓ Saved</span>}
          </div>
        </form>
      </Card>
    </Section>
  );
}

/* ═══════════ CONTRACT ══════════════════════════════════ */
export function Contract() {
  const { hasActiveAccess, effectiveStatus, user } = useAuth();
  const [c, setC] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/contract").then((r) => setC(r.data.contract)).catch(() => {}); }, []);

  if (!hasActiveAccess) {
    return (
      <Section eyebrow="◌ &nbsp; AGREEMENT" title="Master Services Agreement.">
        <GatedNotice kind={effectiveStatus} title="Available with a membership."
          body="The Master Services Agreement formalizes our working relationship. You'll be able to review and sign it once your membership is active." />
      </Section>
    );
  }

  if (!c) return null;
  const signed = c.status === "signed";

  const sign = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await api.post("/contract/sign", { signedName: typed });
      setC(r.data.contract);
    } catch (e) {
      setErr(e?.response?.data?.error || "Couldn't sign right now.");
    } finally { setBusy(false); }
  };

  return (
    <Section eyebrow={signed ? "◉ &nbsp; SIGNED" : "◈ &nbsp; FOR YOUR REVIEW"} title={c.contractTitle}>
      <Card padding={24}>
        <div style={S.contractBox}>
          <pre style={S.contractBody}>{c.contractBody}</pre>
        </div>
        {signed ? (
          <div style={{ ...S.signedNotice, marginTop: 18 }}>
            ✓ Signed by <strong>{c.signedName}</strong> on {new Date(c.signedAt).toLocaleString()}.
          </div>
        ) : (
          <form onSubmit={sign} style={{ marginTop: 22 }}>
            <label style={S.fieldLabel}>Type your full legal name to sign</label>
            <input style={S.input} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={user ? `${user.firstName} ${user.lastName}` : ""} required />
            {err && <div style={{ ...S.eyebrow, color: "#f87171", marginTop: 12 }}>◌ {err}</div>}
            <button type="submit" disabled={busy || typed.trim().length < 3} style={{ ...S.btnDark, marginTop: 18 }}>
              <span>{busy ? "Signing…" : "Sign agreement"}</span><span>→</span>
            </button>
          </form>
        )}
      </Card>
    </Section>
  );
}

/* ═══════════ INVOICES ══════════════════════════════════ */
export function Invoices() {
  const { hasActiveAccess, effectiveStatus } = useAuth();
  const [list, setList] = useState([]);

  useEffect(() => {
    if (!hasActiveAccess) return;
    api.get("/invoices").then((r) => setList(r.data.invoices)).catch(() => {});
  }, [hasActiveAccess]);

  if (!hasActiveAccess) {
    return (
      <Section eyebrow="◌ &nbsp; INVOICES" title="Your billing history.">
        <GatedNotice kind={effectiveStatus} title="Available with a membership."
          body="Once your membership is active, you'll find every invoice here — viewable and downloadable for your records." />
      </Section>
    );
  }

  return (
    <Section eyebrow="◈ &nbsp; INVOICES" title="Your billing history.">
      <Card padding={0}>
        <table style={S.table}>
          <thead><tr style={S.tr}>{["Invoice", "Period", "Amount", "Status", ""].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} style={S.tdEmpty}>No invoices yet.</td></tr>}
            {list.map((inv) => (
              <tr key={inv._id} style={S.tr}>
                <td style={S.td}>{inv.number || inv.stripeInvoiceId.slice(-10)}</td>
                <td style={S.td}>{inv.periodStart ? new Date(inv.periodStart).toLocaleDateString() : "—"}</td>
                <td style={{ ...S.td, fontFamily: "'Inter','DM Sans',system-ui,sans-serif", fontSize: 16, fontWeight: 500, color: C.text }}>${(inv.amountDue / 100).toFixed(2)}</td>
                <td style={S.td}><span style={S.statusPill}>{inv.status}</span></td>
                <td style={{ ...S.td, textAlign: "right" }}>
                  {inv.invoicePdfUrl && <a href={inv.invoicePdfUrl} target="_blank" rel="noreferrer" style={S.linkMono}>download ↗</a>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </Section>
  );
}

/* ═══════════ KICK-OFF SCHEDULER ════════════════════════ */
export function KickOffScheduler() {
  const { hasActiveAccess, effectiveStatus } = useAuth();
  const [data, setData] = useState({ upcoming: [], past: [] });
  const [form, setForm] = useState({ date: "", time: "10:00", durationMin: 60, kind: "kickoff", notes: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const reload = () => api.get("/meetings").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (hasActiveAccess) reload(); }, [hasActiveAccess]);

  if (!hasActiveAccess) {
    return (
      <Section eyebrow="◌ &nbsp; KICK-OFF" title="Schedule your first meeting.">
        <GatedNotice kind={effectiveStatus} title="Available with a membership."
          body="Once you're a member, you can schedule meetings with us — beginning with a kick-off conversation to align on scope and direction." />
      </Section>
    );
  }

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";

  const book = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const start = new Date(`${form.date}T${form.time}:00`);
      const end = new Date(start.getTime() + form.durationMin * 60000);
      await api.post("/meetings", {
        startTime: start.toISOString(), endTime: end.toISOString(),
        kind: form.kind, notes: form.notes,
        title: form.kind === "kickoff" ? "Kick-Off Meeting" : "Meeting",
        timezone: tz,
      });
      setForm({ date: "", time: "10:00", durationMin: 60, kind: "kickoff", notes: "" });
      reload();
    } catch (e) {
      setErr(e?.response?.data?.error || "Couldn't book that time.");
    } finally { setBusy(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this meeting?")) return;
    await api.delete(`/meetings/${id}`).catch(() => {});
    reload();
  };

  const minDate = new Date(); minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().slice(0, 10);

  return (
    <Section eyebrow="◈ &nbsp; SCHEDULE" title={data.upcoming.length === 0 ? "Schedule your kick-off." : "Schedule your next meeting."}>
      {data.upcoming.length > 0 && (
        <Card padding={0} style={{ marginBottom: 20 }}>
          <table style={S.table}>
            <thead><tr style={S.tr}>{["When", "Type", "Status", ""].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.upcoming.map((m) => (
                <tr key={m._id} style={S.tr}>
                  <td style={{ ...S.td, fontFamily: "'Inter','DM Sans',system-ui,sans-serif", fontSize: 15, color: C.text }}>{new Date(m.startTime).toLocaleString()}</td>
                  <td style={S.td}>{m.kind}</td>
                  <td style={S.td}><span style={S.statusPill}>{m.status}</span></td>
                  <td style={{ ...S.td, textAlign: "right" }}><button onClick={() => cancel(m._id)} style={S.linkMono}>cancel</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card padding={22}>
        <form onSubmit={book}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 16 }}>
            <div><label style={S.fieldLabel}>Date</label><input style={S.input} type="date" min={minDateStr} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required /></div>
            <div><label style={S.fieldLabel}>Time</label><input style={S.input} type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required /></div>
            <div><label style={S.fieldLabel}>Duration</label>
              <select style={S.input} value={form.durationMin} onChange={(e) => setForm({ ...form, durationMin: Number(e.target.value) })}>
                <option value={30}>30 min</option><option value={60}>60 min</option><option value={90}>90 min</option>
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <div>
              <label style={S.fieldLabel}>Type</label>
              <select style={S.input} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                <option value="kickoff">Kick-Off</option><option value="checkin">Check-In</option>
                <option value="review">Review</option><option value="strategy">Strategy</option>
              </select>
            </div>
            <div>
              <label style={S.fieldLabel}>Anything we should prepare?</label>
              <textarea style={{ ...S.input, minHeight: 42, resize: "none" }} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          {err && <div style={{ ...S.eyebrow, color: "#f87171", marginBottom: 12 }}>◌ {err}</div>}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <p style={{ ...S.eyebrow, color: C.textMuted }}>One meeting per week · Your local timezone ({tz})</p>
            <button type="submit" disabled={busy} style={S.btnDark}><span>{busy ? "Booking…" : "Confirm meeting"}</span><span>→</span></button>
          </div>
        </form>
      </Card>
    </Section>
  );
}

/* ═══════════ FILE STORAGE ═════════════════════════════ */
export function FileStorage() {
  const [files, setFiles] = useState([]);
  const [canUpload, setCanUpload] = useState(false);

  const reload = () => api.get("/files").then((r) => { setFiles(r.data.files); setCanUpload(r.data.canUpload); }).catch(() => {});
  useEffect(() => { reload(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this file?")) return;
    await api.delete(`/files/${id}`).catch(() => {});
    reload();
  };

  return (
    <Section eyebrow="◈ &nbsp; FILE STORAGE" title="Your shared files."
      right={canUpload && <span style={S.eyebrow}>Upload via the button below</span>}>
      {files.length === 0 ? (
        <Card>
          <p style={{ fontSize: 13, color: C.textMuted, textAlign: "center", padding: "12px 0" }}>
            {canUpload ? "Drop your first file when you're ready — assets, briefs, brand guides, anything we should see." : "Files you upload will appear here."}
          </p>
        </Card>
      ) : (
        <Card padding={0}>
          <table style={S.table}>
            <thead><tr style={S.tr}>{["File", "Size", "Type", "Added", ""].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {files.map((f) => (
                <tr key={f._id} style={S.tr}>
                  <td style={S.td}><a href={f.url} target="_blank" rel="noreferrer" style={S.linkInline}>{f.originalName}</a></td>
                  <td style={S.td}>{(f.size / 1024).toFixed(0)} KB</td>
                  <td style={S.td}>{f.mimeType}</td>
                  <td style={S.td}>{new Date(f.createdAt).toLocaleDateString()}</td>
                  <td style={{ ...S.td, textAlign: "right" }}>{canUpload && <button onClick={() => remove(f._id)} style={S.linkMono}>remove</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {canUpload && <FileUploader onDone={reload} />}
    </Section>
  );
}

// File upload — POSTs multipart to /files/upload (your S3 route).
// If that route isn't wired yet, the button still renders and shows
// a friendly note when the request fails.
function FileUploader({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone();
    } catch (er) {
      setErr(er?.response?.data?.error || "Upload route not wired yet — coming soon.");
    } finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <div style={{ marginTop: 18 }}>
      <label style={{ ...S.btnDark, cursor: "pointer", display: "inline-flex" }}>
        <span>{busy ? "Uploading…" : "+ Upload file"}</span><span>↑</span>
        <input type="file" onChange={handleFile} style={{ display: "none" }} disabled={busy} />
      </label>
      {err && <div style={{ ...S.eyebrow, color: "#a64a2a", marginTop: 10 }}>◌ {err}</div>}
    </div>
  );
}

/* ═══════════ WEBSITES TABLE ════════════════════════════ */
const WS_LABEL = { active: "Active", inactive: "Inactive", in_development: "In Development", in_production: "In Production" };

export function Websites() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/websites").then((r) => setList(r.data.websites)).catch(() => {}); }, []);

  return (
    <Section eyebrow="◈ &nbsp; YOUR PROJECTS" title="Websites in motion.">
      {list.length === 0 ? (
        <div style={S.empty}>Your projects will appear here once we get started.</div>
      ) : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr style={S.tr}>{["Project", "URL", "Status", "Updated"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {list.map((w) => (
                <tr key={w._id} style={S.tr}>
                  <td style={{ ...S.td, fontFamily: "'Inter','DM Sans',system-ui,sans-serif", fontSize: 16, fontWeight: 500, color: C.text }}>{w.name}</td>
                  <td style={S.td}>{w.url ? <a href={w.url} target="_blank" rel="noreferrer" style={S.linkInline}>{w.url.replace(/^https?:\/\//, "")} ↗</a> : "—"}</td>
                  <td style={S.td}><span style={S.statusPill}>{WS_LABEL[w.status] || w.status}</span></td>
                  <td style={S.td}>{new Date(w.updatedAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

/* ═══════════ PLANS (free-tier upsell) ══════════════════ */
const PLANS = [
  { tier: "01", name: "Discovery & Strategy", price: "$199", cad: "one-time",  envKey: "REACT_APP_STRIPE_DEV_STRATEGY_CALL", priceKey: "STRIPE_DEV_STRATEGY_CALL" },
  { tier: "02", name: "Launchpad",            price: "$400", cad: "/ month",   envKey: "REACT_APP_STRIPE_DEV_PKG_A",         priceKey: "STRIPE_DEV_PKG_A", featured: true },
  { tier: "03", name: "Architect",            price: "$1,200", cad: "/ month", envKey: "REACT_APP_STRIPE_DEV_PKG_B",         priceKey: "STRIPE_DEV_PKG_B" },
  { tier: "04", name: "Omni Platform",        price: "From $3,500", cad: "/ month", envKey: "REACT_APP_STRIPE_DEV_PKG_C",     priceKey: "STRIPE_DEV_PKG_C" },
];

export function Plans() {
  const { hasActiveAccess } = useAuth();
  const [busy, setBusy] = useState("");
  const [openPlan, setOpenPlan] = useState(null); // accordion state

  if (hasActiveAccess) return null;

  const checkout = async (priceKey) => {
    setBusy(priceKey);
    try {
      const r = await api.post("/stripe/create-checkout-session", { priceKey });
      if (r.data.url) window.location.href = r.data.url;
    } catch { setBusy(""); }
  };

  const PLAN_DETAILS = {
    STRIPE_DEV_STRATEGY_CALL: [
      "60-minute discovery call with our team",
      "Full project scope + opportunity assessment",
      "Written roadmap delivered within 48 hours",
      "Priority onboarding if you choose a plan",
    ],
    STRIPE_DEV_PKG_A: [
      "Multi-page responsive website",
      "Custom domain + SSL setup",
      "Contact forms + basic integrations",
      "Monthly content updates included",
      "Priority support via dashboard",
    ],
    STRIPE_DEV_PKG_B: [
      "Everything in Launchpad",
      "Custom API integrations + backend logic",
      "Admin dashboard for your team",
      "Performance optimization + analytics",
      "Bi-weekly check-in calls",
    ],
    STRIPE_DEV_PKG_C: [
      "Everything in Architect",
      "iOS + Android mobile applications",
      "Multi-platform deployment pipeline",
      "Dedicated project manager",
      "Custom SLA + white-glove support",
    ],
  };

  return (
    <section style={{ marginTop: -36, marginBottom: 56 }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }} aria-hidden="true">
        <div style={{ width: 1, height: 32, background: "rgba(255,255,255,0.08)" }} />
      </div>

      <div style={{ marginBottom: 24 }}>
        <div style={S.eyebrow}>◌ &nbsp; ACTIVATE TO CONTINUE</div>
        <h2 style={S.h2}>Choose a path to begin your roadmap.</h2>
      </div>

      {/* Vertical accordion */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {PLANS.map((p) => {
          const isOpen = openPlan === p.priceKey;
          const details = PLAN_DETAILS[p.priceKey] || [];
          return (
            <div key={p.tier} style={{
              position: "relative",
              background: isOpen ? C.glassStrong : C.glass,
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
              border: isOpen ? `1px solid ${C.borderMed}` : `1px solid ${C.border}`,
              borderRadius: 16,
              overflow: "hidden",
              boxShadow: isOpen ? `0 8px 32px rgba(0,0,0,0.3), ${C.innerGlow}` : C.innerGlow,
              transition: "all 240ms cubic-bezier(0.22,1,0.36,1)",
            }}>
              {/* Gold gradient overlay */}
              <div style={{ position: "absolute", inset: 0, background: GOLD_GRAD, pointerEvents: "none", zIndex: 0 }} />
              {/* Row header — always visible, click to toggle */}
              <button
                onClick={() => setOpenPlan(isOpen ? null : p.priceKey)}
                style={{
                  position: "relative", zIndex: 1,
                  width: "100%", display: "flex", alignItems: "center",
                  justifyContent: "space-between",
                  padding: "18px 22px",
                  background: "none", border: "none", cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                  <span style={{ ...S.eyebrow, minWidth: 22 }}>{p.tier}</span>
                  <span style={{ fontFamily: "'Inter','DM Sans',system-ui,sans-serif", fontSize: 16, fontWeight: 500, color: C.text }}>{p.name}</span>
                  {p.featured && (
                    <span style={{ ...S.eyebrow, color: C.accent, border: `1px solid rgba(229,211,163,0.25)`, padding: "2px 9px", borderRadius: 999, fontSize: 8 }}>RECOMMENDED</span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 20, flexShrink: 0 }}>
                  <span style={{ fontFamily: "'Inter','DM Sans',system-ui,sans-serif", fontSize: 18, fontWeight: 300, color: isOpen ? C.accent : C.text, letterSpacing: "-0.01em" }}>
                    {p.price} <span style={{ ...S.eyebrow, fontSize: 9, letterSpacing: "0.2em" }}>{p.cad}</span>
                  </span>
                  <span style={{ color: C.textMuted, fontSize: 14, transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 240ms ease", display: "inline-block" }}>&#x25BE;</span>
                </div>
              </button>

              {/* Expanded details */}
              {isOpen && (
                <div style={{ position: "relative", zIndex: 1, padding: "0 22px 22px" }}>
                  <div style={{ borderTop: `1px solid rgba(255,255,255,0.08)`, paddingTop: 18, marginBottom: 18 }}>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                      {details.map((d, i) => (
                        <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                          <span style={{ color: C.accent, flexShrink: 0, fontSize: 12, marginTop: 1 }}>&#9656;</span>
                          <span style={{ fontSize: 14, color: C.textSec, lineHeight: 1.5 }}>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button
                    onClick={() => checkout(p.priceKey)}
                    disabled={busy === p.priceKey}
                    style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      width: "100%", padding: "12px 18px",
                      fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
                      background: p.featured ? "rgba(229,211,163,0.14)" : "rgba(255,255,255,0.07)",
                      color: p.featured ? C.accent : C.text,
                      border: p.featured ? `1px solid rgba(229,211,163,0.28)` : `1px solid rgba(255,255,255,0.12)`,
                      borderRadius: 10, cursor: "pointer",
                    }}
                  >
                    <span>{busy === p.priceKey ? "Opening…" : `Activate ${p.name}`}</span>
                    <span>→</span>
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ─── Shared styles ────────────────────────────────────── */
const S = {
  eyebrow: {
    fontFamily: "'DM Mono', monospace", fontSize: 9,
    letterSpacing: "0.28em", textTransform: "uppercase", color: C.textMuted,
  },
  h2: {
    fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    fontWeight: 300, fontSize: 28, lineHeight: 1.15,
    color: C.text, letterSpacing: "-0.01em", marginTop: 6,
  },
  lede: { color: C.textSec, fontSize: 14, lineHeight: 1.7 },

  fieldLabel: {
    display: "block",
    fontFamily: "'DM Mono', monospace", fontSize: 9,
    letterSpacing: "0.24em", textTransform: "uppercase",
    color: C.textMuted, marginBottom: 7,
  },
  input: {
    width: "100%",
    background: "rgba(255,255,255,0.04)",
    border: `1px solid rgba(255,255,255,0.09)`,
    borderRadius: 10,
    padding: "10px 13px",
    fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    fontSize: 14, color: C.text, outline: "none",
    transition: "border-color 240ms ease",
  },
  btnDark: {
    padding: "10px 20px",
    fontFamily: "'DM Mono', monospace", fontSize: 10,
    letterSpacing: "0.18em", textTransform: "uppercase",
    background: "rgba(255,255,255,0.07)", color: C.text,
    border: `1px solid rgba(255,255,255,0.13)`,
    borderRadius: 999, cursor: "pointer",
    display: "inline-flex", gap: 10, alignItems: "center",
  },

  // Roadmap milestone strip
  msStrip: {
    display: "flex", marginTop: 18, gap: 8,
    padding: "16px 0 0",
    borderTop: `1px solid rgba(255,255,255,0.05)`,
  },
  msStripCell: { flex: 1, textAlign: "center" },
  barOuter: { width: "100%", height: 2, background: "rgba(255,255,255,0.07)", borderRadius: 999, position: "relative" },
  barInner: { position: "absolute", left: 0, top: 0, height: "100%", background: "rgba(229,211,163,0.5)", borderRadius: 999, transition: "width 0.4s cubic-bezier(0.22,1,0.36,1)" },
  notes: {
    background: "rgba(255,255,255,0.03)",
    border: `1px solid rgba(255,255,255,0.06)`,
    borderRadius: 10,
    padding: "14px 18px",
    fontSize: 13, color: C.textSec, lineHeight: 1.7,
  },

  // Tables (used inside Card components)
  table: { width: "100%", borderCollapse: "collapse" },
  tr: { borderBottom: `1px solid rgba(255,255,255,0.05)` },
  th: {
    fontFamily: "'DM Mono', monospace", fontSize: 9,
    letterSpacing: "0.22em", textTransform: "uppercase",
    color: C.textMuted, textAlign: "left",
    padding: "12px 18px", fontWeight: 400,
    background: "rgba(255,255,255,0.02)",
  },
  td: { padding: "13px 18px", fontSize: 13, color: C.textSec },
  tdEmpty: { padding: "32px 18px", fontSize: 13, color: C.textMuted, textAlign: "center" },
  statusPill: {
    fontFamily: "'DM Mono', monospace", fontSize: 9,
    letterSpacing: "0.18em", textTransform: "uppercase",
    padding: "3px 10px",
    border: `1px solid rgba(255,255,255,0.10)`,
    borderRadius: 999, color: C.textMuted,
    background: "rgba(255,255,255,0.03)",
  },

  // Contract
  contractBox: {
    background: "rgba(255,255,255,0.02)",
    border: `1px solid rgba(255,255,255,0.06)`,
    borderRadius: 10,
    padding: 20, maxHeight: 420, overflowY: "auto",
  },
  contractBody: {
    fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    fontSize: 13, lineHeight: 1.8, color: C.textSec,
    whiteSpace: "pre-wrap", margin: 0,
  },
  signedNotice: {
    padding: "13px 16px",
    background: "rgba(131,242,143,0.07)",
    border: `1px solid rgba(131,242,143,0.18)`,
    borderRadius: 10,
    fontFamily: "'DM Mono', monospace", fontSize: 11,
    letterSpacing: "0.14em", color: C.accentGreen,
  },

  // Inline text links / actions
  linkMono: {
    fontFamily: "'DM Mono', monospace", fontSize: 10,
    letterSpacing: "0.18em", color: C.textMuted,
    textDecoration: "none", background: "none", border: "none", cursor: "pointer",
  },
  linkInline: { color: C.textSec, textDecoration: "underline", textUnderlineOffset: 3 },

  // Gated notices (content only — Card provides the wrapper)
  gatedH: {
    fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    fontWeight: 500, fontSize: 18,
    color: C.text, marginBottom: 8,
  },
  gatedBody: { color: C.textSec, fontSize: 14, lineHeight: 1.7, maxWidth: 520 },
};
